import React, { useState, useEffect } from 'react';
import { useData } from '../../../context/DataContext';
import { farmerApi, companyApi, mappingApi } from '../../../services/api';

const MappingForm = ({ onSubmit, onCancel }) => {
  const { companies: contextCompanies, farmers: contextFarmers } = useData();
  const [selectedFarmers, setSelectedFarmers] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch farmers
      const farmersResponse = await farmerApi.getAllFarmers();
      const farmersData = Array.isArray(farmersResponse) ? farmersResponse : [];
      
      // Fetch companies
      const companiesResponse = await companyApi.getAllCompanies();
      const companiesData = companiesResponse.companies || [];

      // Update the context data
      if (farmersData.length > 0) {
        const newFarmers = farmersData.map(farmer => ({
          id: farmer.id,
          name: farmer.name || farmer.farmer_name || 'Unnamed Farmer',
          village: farmer.village || '',
          district: farmer.district || '',
          location: farmer.location || ''
        }));
        contextFarmers.splice(0, contextFarmers.length, ...newFarmers);
      }

      if (companiesData.length > 0) {
        const newCompanies = companiesData.map(company => ({
          id: company.id,
          name: company.name || company.company_name || 'Unnamed Company',
          email: company.email || '',
          phone: company.phone || company.mobile || '',
          industry: company.industry || '',
          location: company.location || company.address || ''
        }));
        contextCompanies.splice(0, contextCompanies.length, ...newCompanies);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.response?.data?.detail || error.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!selectedCompany || selectedFarmers.length === 0) {
      setError('Please select both a company and at least one farmer');
      return;
    }

    try {
      // Get the full farmer objects for the selected IDs
      const selectedFarmerObjects = contextFarmers.filter(farmer => 
        selectedFarmers.includes(farmer.id)
      );

      const mappingData = {
        company: selectedCompany,
        farmers: selectedFarmerObjects
      };
      await onSubmit(mappingData);
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setError(error.message || 'An error occurred while creating mappings');
    }
  };

  const handleFarmerToggle = (farmerId) => {
    setSelectedFarmers(prev =>
      prev.includes(farmerId)
        ? prev.filter(id => id !== farmerId)
        : [...prev, farmerId]
    );
  };

  const filteredFarmers = contextFarmers.filter(farmer =>
    (farmer.name || farmer.farmer_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (farmer.village || farmer.district || farmer.location || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div style={styles.loading}>Loading...</div>;
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  return (
    <div style={styles.container}>
      <form onSubmit={handleSubmit}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Select Company:</label>
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            style={styles.select}
            required
          >
            <option value="">Select a company</option>
            {contextCompanies.map(company => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Search Farmers:</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name or location..."
            style={styles.searchInput}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Select Farmers:</label>

          <div style={styles.farmerList}>
            {filteredFarmers.length > 0 ? (
              filteredFarmers.map(farmer => (
                <div key={farmer.id} style={styles.farmerItem}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={selectedFarmers.includes(farmer.id)}
                      onChange={() => handleFarmerToggle(farmer.id)}
                      style={styles.checkbox}
                    />
                    <span style={styles.farmerName}>{farmer.name || farmer.farmer_name || 'Unnamed Farmer'}</span>
                    <span style={styles.farmerLocation}>
                      {farmer.village || farmer.district || farmer.location || 'Unknown Location'}
                    </span>
                  </label>
                </div>
              ))
            ) : (
              <div style={styles.noResults}>
                No farmers found matching your criteria.
              </div>
            )}
          </div>

          <div style={styles.selectedCount}>
            Selected: {selectedFarmers.length} farmers
          </div>
        </div>

        <div style={styles.buttonGroup}>
          <button type="button" onClick={onCancel} style={styles.cancelButton}>
            Cancel
          </button>
          <button type="submit" style={styles.submitButton} disabled={loading}>
            {loading ? 'Creating...' : 'Create Mapping'}
          </button>
        </div>
      </form>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: 'var(--card-bg)',
    borderRadius: '12px',
    padding: '2rem',
    boxShadow: 'var(--card-shadow)',
    width: '100%',
  },
  title: {
    marginTop: 0,
    marginBottom: '1.5rem',
    color: 'var(--primary-dark)',
    textAlign: 'center',
    fontSize: '1.8rem',
    fontWeight: 'bold',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  formGroup: {
    marginBottom: '1.5rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.75rem',
    fontWeight: '600',
    color: 'var(--text-color)',
    fontSize: '1rem',
  },
  select: {
    width: '100%',
    padding: '0.85rem 1rem',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    fontSize: '1rem',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    transition: 'border-color 0.3s, box-shadow 0.3s',
  },
  searchInput: {
    width: '100%',
    padding: '0.85rem 1rem',
    borderRadius: '8px',
    border: '1px solid var(--border-color)',
    fontSize: '1rem',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },
  farmerList: {
    maxHeight: '400px',
    overflowY: 'auto',
    border: '1px solid var(--border-color)',
    borderRadius: '8px',
    padding: '0.75rem',
    backgroundColor: 'white',
    boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.05)',
  },
  farmerItem: {
    padding: '0.75rem',
    borderBottom: '1px solid var(--border-color)',
    transition: 'background-color 0.2s',
    borderRadius: '4px',
    margin: '0.25rem 0',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    width: '100%',
  },
  checkbox: {
    marginRight: '0.75rem',
    width: '18px',
    height: '18px',
    accentColor: 'var(--primary-color)',
  },
  farmerName: {
    flex: 1,
    fontWeight: '600',
    color: 'var(--text-color)',
  },
  farmerLocation: {
    color: 'var(--text-light)',
    fontSize: '0.9rem',
    backgroundColor: 'var(--background-color)',
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    marginLeft: '0.5rem',
  },
  selectedCount: {
    marginTop: '0.75rem',
    textAlign: 'right',
    color: 'var(--primary-color)',
    fontSize: '0.9rem',
    fontWeight: '600',
  },
  noResults: {
    padding: '2rem',
    textAlign: 'center',
    color: 'var(--text-light)',
    fontSize: '1rem',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '1.5rem',
    gap: '1rem',
  },
  cancelButton: {
    padding: '0.85rem 1.5rem',
    backgroundColor: 'var(--text-light)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
    flex: '1 1 auto',
  },
  submitButton: {
    padding: '0.85rem 1.5rem',
    backgroundColor: 'var(--secondary-color)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
    flex: '2 1 auto',
    backgroundImage: 'linear-gradient(135deg, var(--secondary-color) 0%, var(--secondary-dark) 100%)',
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: 'var(--text-light)',
    fontSize: '1rem',
  },
  error: {
    padding: '2rem',
    textAlign: 'center',
    color: 'var(--text-light)',
    fontSize: '1rem',
  },
};

export default MappingForm;



